import { useCallback, useEffect, useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import { mfgInlineInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { NamedVariantMaterialsEffect } from "@/lib/manufacturing/namedVariantMaterialsEffect";
import type { NamedVariantApplicationSummary } from "@/lib/manufacturing/namedVariantsClient";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { ManufacturingNamedVariantApplicationRow } from "./ManufacturingNamedVariantApplicationRow";
import { ManufacturingNamedVariantApplicationsBulkBar } from "./ManufacturingNamedVariantApplicationsBulkBar";

export type NamedVariantApplicationConfirmFilter = "all" | "unconfirmed" | "confirmed";

const CONFIRM_FILTERS: Array<{ id: NamedVariantApplicationConfirmFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "unconfirmed", label: "Unconfirmed" },
  { id: "confirmed", label: "Confirmed" },
];

function isPending(app: NamedVariantApplicationSummary): boolean {
  return app.confirm_status === "pending";
}

export function ManufacturingNamedVariantApplicationsList({
  namedVariantId,
  applications,
  templateMode = "delta",
  materials = [],
  orgSlotLabels = [],
  saveBusy,
  previewBusy,
  onPreview,
  onConfirm,
  onConfirmMany,
  onDelete,
  onUpdatePrices,
  onUpdateEffect,
}: {
  /** When this changes, status filter resets to All. Confirm/reload must not change it. */
  namedVariantId: string | null;
  applications: NamedVariantApplicationSummary[];
  /** Authoritative materials mode from the named-variant template. */
  templateMode?: NamedVariantMaterialsEffect["mode"];
  materials?: readonly MfgMaterial[];
  orgSlotLabels?: readonly string[];
  saveBusy: boolean;
  previewBusy: boolean;
  onPreview: (applicationId: string) => void;
  onConfirm: (applicationId: string) => void;
  onConfirmMany: (applicationIds: readonly string[]) => void;
  onDelete: (applicationId: string) => void;
  onUpdatePrices: (
    applicationId: string,
    patch: {
      wholesale_price_delta?: number | null;
      retail_price_delta?: number | null;
      included_by_default?: boolean;
    },
  ) => void;
  onUpdateEffect: (
    applicationId: string,
    materials_effect: NamedVariantMaterialsEffect,
  ) => void;
}) {
  const [confirmFilter, setConfirmFilter] =
    useState<NamedVariantApplicationConfirmFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /** Confirmed apps hide the editor until the user re-opens it. */
  const [editingEffectIds, setEditingEffectIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Reset only when opening a different variant — not on confirm or Hide/Show.
  useEffect(() => {
    setConfirmFilter("all");
    setSelectedIds(new Set());
  }, [namedVariantId]);

  const filtered = useMemo(() => {
    if (confirmFilter === "confirmed") {
      return applications.filter((a) => a.confirm_status === "confirmed");
    }
    if (confirmFilter === "unconfirmed") {
      return applications.filter((a) => a.confirm_status !== "confirmed");
    }
    return applications;
  }, [applications, confirmFilter]);

  const pendingFiltered = useMemo(() => filtered.filter(isPending), [filtered]);

  const pager = usePagination(filtered, 5);

  useEffect(() => {
    pager.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset page on filter change only
  }, [confirmFilter]);

  // Drop selection / expand / edit state for apps that disappeared or are no longer pending.
  useEffect(() => {
    if (expandedId && !applications.some((a) => a.id === expandedId)) {
      setExpandedId(null);
    }
    setEditingEffectIds((prev) => {
      const next = new Set([...prev].filter((id) => applications.some((a) => a.id === id)));
      return next.size === prev.size ? prev : next;
    });
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(
        [...prev].filter((id) => applications.some((a) => a.id === id && isPending(a))),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [applications, expandedId]);

  const allPendingFilteredSelected =
    pendingFiltered.length > 0 && pendingFiltered.every((a) => selectedIds.has(a.id));

  const toggleSelected = useCallback((applicationId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(applicationId)) next.delete(applicationId);
      else next.add(applicationId);
      return next;
    });
  }, []);

  const selectAllPendingFiltered = useCallback(() => {
    setSelectedIds(new Set(pendingFiltered.map((a) => a.id)));
  }, [pendingFiltered]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  function toggleEditEffect(applicationId: string) {
    setEditingEffectIds((prev) => {
      const next = new Set(prev);
      if (next.has(applicationId)) next.delete(applicationId);
      else next.add(applicationId);
      return next;
    });
  }

  function confirmSelected() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    onConfirmMany(ids);
    setSelectedIds(new Set());
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-400">
          <span className="font-medium">Status</span>
          <select
            className={mfgInlineInputClass}
            value={confirmFilter}
            onChange={(e) =>
              setConfirmFilter(e.target.value as NamedVariantApplicationConfirmFilter)
            }
          >
            {CONFIRM_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          {filtered.length} of {applications.length}
        </span>
      </div>

      <ManufacturingNamedVariantApplicationsBulkBar
        selectedCount={selectedIds.size}
        pendingFilteredCount={pendingFiltered.length}
        allPendingFilteredSelected={allPendingFilteredSelected}
        saveBusy={saveBusy}
        onSelectAllPendingFiltered={selectAllPendingFiltered}
        onClear={clearSelection}
        onConfirmSelected={confirmSelected}
      />

      <ul className="space-y-2">
        {pager.paginatedItems.map((app) => {
          const selectable = isPending(app);
          return (
            <ManufacturingNamedVariantApplicationRow
              key={app.id}
              app={app}
              expanded={expandedId === app.id}
              selected={selectedIds.has(app.id)}
              selectable={selectable}
              showEffectEditor={
                app.confirm_status === "pending" || editingEffectIds.has(app.id)
              }
              templateMode={templateMode}
              materials={materials}
              orgSlotLabels={orgSlotLabels}
              saveBusy={saveBusy}
              previewBusy={previewBusy}
              onToggleExpanded={() => setExpandedId(expandedId === app.id ? null : app.id)}
              onToggleSelected={() => toggleSelected(app.id)}
              onPreview={() => onPreview(app.id)}
              onConfirm={() => onConfirm(app.id)}
              onDelete={() => onDelete(app.id)}
              onUpdatePrices={(patch) => onUpdatePrices(app.id, patch)}
              onUpdateEffect={(effect) => onUpdateEffect(app.id, effect)}
              onToggleEditEffect={() => toggleEditEffect(app.id)}
            />
          );
        })}
        {applications.length === 0 ? (
          <li className="text-sm text-gray-500">No applications yet.</li>
        ) : filtered.length === 0 ? (
          <li className="text-sm text-gray-500">No applications match this filter.</li>
        ) : null}
      </ul>

      {filtered.length > 5 ? (
        <PaginationBar
          pageIndex={pager.pageIndex}
          pageCount={pager.pageCount}
          pageSize={pager.pageSize}
          totalCount={pager.totalCount}
          firstShown={pager.firstShown}
          lastShown={pager.lastShown}
          onPageChange={pager.setPageIndex}
          onPageSizeChange={pager.setPageSize}
          noun="applications"
          alwaysShow
        />
      ) : null}
    </div>
  );
}
